/**
 * WebSocket client for real-time board updates.
 * Connects to API Gateway WebSocket API, handles reconnection with exponential backoff.
 *
 * The connection URL (including the auth token) is resolved fresh on every
 * connect attempt via the `getUrl` provider. This ensures reconnects after a
 * token expiry (e.g. laptop wake-from-sleep) use a refreshed token rather than
 * a stale one baked in at construction time. If `getUrl` returns null, the
 * session is considered expired and no further reconnect attempts are made.
 */

export type WebSocketState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'session-expired';

export interface WebSocketClientConfig {
  readonly boardId: string;
  /**
   * Resolves the full WebSocket URL (with a fresh token) for a connect attempt.
   * Return null when the session can no longer be authenticated — the client
   * will stop retrying and transition to 'session-expired'.
   */
  readonly getUrl: () => Promise<string | null>;
  readonly createSocket: (url: string) => WebSocket;
  readonly onEvent: (event: Record<string, unknown>) => void;
  readonly onStateChange: (state: WebSocketState) => void;
  readonly onRefetchNeeded?: () => void;
  /**
   * Called once the socket opens, with a `send` helper for the active socket.
   * Lets a subscriber register itself on (re)connect — e.g. page subscriptions
   * send a `subscribePage` action here so the subscription is re-established
   * after every reconnect, not just the first connect. Optional: the board
   * path (whose subscription is bound at $connect via the boardId query param)
   * does not provide it, so its behavior is unchanged.
   */
  readonly onConnect?: (send: (message: string) => void) => void;
  /** Called when the session cannot be re-authenticated (getUrl returned null). */
  readonly onSessionExpired?: () => void;
  readonly maxRetries?: number;
}

export interface WebSocketClient {
  connect(): void;
  disconnect(): void;
  /** Force an immediate reconnect (resets backoff). Used for wake-from-sleep. */
  reconnectNow(): void;
  getState(): WebSocketState;
  getLastEventTimestamp(): string | null;
  getLastSeq(): number;
}

const DEFAULT_MAX_RETRIES = 10;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;

export function createWebSocketClient(config: WebSocketClientConfig): WebSocketClient {
  const maxRetries = config.maxRetries ?? DEFAULT_MAX_RETRIES;
  let state: WebSocketState = 'disconnected';
  let socket: WebSocket | null = null;
  let retryCount = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let intentionalClose = false;
  let lastEventTimestamp: string | null = null;
  let lastSeq = 0;

  function setState(newState: WebSocketState): void {
    state = newState;
    config.onStateChange(newState);
  }

  function connect(): void {
    intentionalClose = false;
    retryCount = 0;
    void doConnect();
  }

  async function doConnect(): Promise<void> {
    setState('connecting');

    // Resolve a fresh URL (with a fresh token) for this attempt. If the token
    // cannot be refreshed, treat the session as expired and stop retrying.
    let url: string | null;
    try {
      url = await config.getUrl();
    } catch {
      url = null;
    }

    if (intentionalClose) {
      // disconnect() was called while we were awaiting the token.
      return;
    }

    if (!url) {
      setState('session-expired');
      config.onSessionExpired?.();
      return;
    }

    socket = config.createSocket(url);

    socket.onopen = () => {
      retryCount = 0;
      setState('connected');

      // Let the subscriber (re)register on every (re)connect, e.g. subscribePage.
      if (socket) {
        const activeSocket = socket;
        config.onConnect?.((message: string) => activeSocket.send(message));
      }

      // Send catch-up request on reconnection (skip on first fresh connection)
      if (lastSeq > 0 && socket) {
        socket.send(JSON.stringify({ action: 'catchup', lastSeq }));
      }
    };

    socket.onmessage = (event: MessageEvent | { data: string }) => {
      try {
        const data = JSON.parse(typeof event === 'object' && 'data' in event ? (event as { data: string }).data : '');

        if (data.action === 'catchup_response') {
          const currentSeq = typeof data.currentSeq === 'number' ? data.currentSeq : 0;

          if (currentSeq - lastSeq > 100) {
            // Gap too large — trigger full board refetch
            config.onRefetchNeeded?.();
          } else {
            // Replay missed events in seq order
            const events = Array.isArray(data.events) ? data.events : [];
            for (const evt of events) {
              config.onEvent(evt);
            }
          }

          lastSeq = currentSeq;
          return;
        }

        // Normal domain event
        if (data.timestamp) lastEventTimestamp = data.timestamp;
        if (typeof data.seq === 'number') lastSeq = data.seq;
        config.onEvent(data);
      } catch {
        // Ignore malformed messages
      }
    };

    socket.onclose = () => {
      if (intentionalClose) {
        setState('disconnected');
        return;
      }
      scheduleReconnect();
    };

    socket.onerror = () => {
      // onclose will fire after onerror
    };
  }

  function scheduleReconnect(): void {
    if (retryCount >= maxRetries) {
      setState('disconnected');
      return;
    }

    setState('reconnecting');
    const delay = Math.min(BASE_DELAY_MS * Math.pow(2, retryCount), MAX_DELAY_MS);
    retryCount++;

    retryTimer = setTimeout(() => {
      void doConnect();
    }, delay);
  }

  function reconnectNow(): void {
    // Skip if we're already connected or actively connecting.
    if (state === 'connected' || state === 'connecting') return;

    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    if (socket) {
      // Detach handlers so the pending close doesn't schedule another reconnect.
      socket.onclose = null;
      socket.onerror = null;
      socket.onmessage = null;
      socket.onopen = null;
      try { socket.close(); } catch { /* ignore */ }
      socket = null;
    }
    intentionalClose = false;
    retryCount = 0;
    void doConnect();
  }

  function disconnect(): void {
    intentionalClose = true;
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    if (socket) {
      socket.close();
      socket = null;
    }
    setState('disconnected');
  }

  function getState(): WebSocketState {
    return state;
  }

  function getLastEventTimestamp(): string | null {
    return lastEventTimestamp;
  }

  function getLastSeq(): number {
    return lastSeq;
  }

  return { connect, disconnect, reconnectNow, getState, getLastEventTimestamp, getLastSeq };
}
